// How this browser last signed in (the login page's "Last used"), kept beside the
// remembered email that Remember me stores, and only ever for that email.
//
// Tying it to `m8_email` is what keeps it honest on shared computers: a method is
// written only when the account that just signed in is the one Remember me kept,
// so it is never shown next to someone else's email, and it goes wherever that
// email goes (kept across sign-out, removed when Remember me is turned off).
// Nothing here is secret: the value only chooses which sign-in option the login
// page puts first.

export const REMEMBERED_EMAIL_KEY = 'm8_email';
export const LAST_LOGIN_METHOD_KEY = 'm8_last_login_method';
// Both travel together through clearM8LocalStorage's preserveEmail.
export const REMEMBERED_SIGN_IN_KEYS = [REMEMBERED_EMAIL_KEY, LAST_LOGIN_METHOD_KEY];

// Api::V1::UsersController#show `sign_in_method`.
export const LOGIN_METHODS = {
  saml: 'saml',
  microsoft: 'microsoft',
  password: 'password',
};
const KNOWN_METHODS = Object.values(LOGIN_METHODS);

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const sameEmail = (a, b) => (
  typeof a === 'string' && typeof b === 'string' && a.trim() !== ''
  && a.trim().toLowerCase() === b.trim().toLowerCase()
);

/**
 * The remembered email and how it last signed in, or nulls.
 *
 * @returns {{ email: string|null, method: string|null }}
 */
export const loadRememberedSignIn = () => {
  const email = read(REMEMBERED_EMAIL_KEY) || null;
  const method = read(LAST_LOGIN_METHOD_KEY);
  return { email, method: email && KNOWN_METHODS.includes(method) ? method : null };
};

/**
 * Records how `email` just signed in, when it is the remembered email and this is
 * not an impersonation. An unknown method (an older backend) records nothing.
 *
 * @param {Object} signIn
 * @param {string} signIn.method one of LOGIN_METHODS
 * @param {string} signIn.email the signed-in user's email
 * @param {boolean} [signIn.impersonating]
 * @returns {boolean} whether it was recorded
 */
export const rememberLoginMethod = ({ method, email, impersonating = false }) => {
  if (impersonating || !KNOWN_METHODS.includes(method)) return false;
  if (!sameEmail(read(REMEMBERED_EMAIL_KEY), email)) return false;

  try {
    localStorage.setItem(LAST_LOGIN_METHOD_KEY, method);
    return true;
  } catch {
    return false;
  }
};

/**
 * Remember me: keeps `email` for the next sign-in. A different email than the one
 * kept drops the old one's "Last used", which belonged to that other account.
 *
 * @param {string} email
 */
export const rememberEmail = (email) => {
  try {
    if (!sameEmail(read(REMEMBERED_EMAIL_KEY), email)) localStorage.removeItem(LAST_LOGIN_METHOD_KEY);
    localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
  } catch {
    // Storage may be unavailable (private browsing); the sign-in still works.
  }
};

// Remember me turned off, or "Not you?": the next person sees an empty login.
export const forgetRememberedSignIn = () => {
  REMEMBERED_SIGN_IN_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage may be unavailable (private browsing); nothing to forget then.
    }
  });
};
